import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Map } from 'immutable';
import classnames from 'classnames';
import {
  MOBILE_MEDIA_QUERY,
  Sidebar,
  SidebarExpandableItem,
  SidebarExpandableMenuItem,
  SidebarMenuItem,
  SidebarMenuItemChild,
} from '@saucelabs/ds-navigation';
import { constants as tunnelsConstants } from '@saucelabs/sl-module-tunnels';
import {
  IconAnalyticsFill,
  IconAnalyticsOutline,
  IconApiTestingFill,
  IconApiTestingOutline,
  IconAppForegroundFill,
  IconAppForegroundOutline,
  IconAutomatedFill,
  IconAutomatedOutline,
  IconBellFill,
  IconBellOutline,
  IconCheckmarkFill,
  IconDashboardFill,
  IconDashboardOutline,
  IconDevicePhoneFill,
  IconDevicePhoneOutline,
  IconErrorReportingFill,
  IconErrorReportingOutline,
  IconExternalOutline,
  IconGettingStartedFill,
  IconGettingStartedOutline,
  IconGroupFill,
  IconGroupOutline,
  IconManualFill,
  IconManualOutline,
  IconQuestionFill,
  IconQuestionOutline,
  IconSearchFill,
  IconSearchOutline,
  IconTestSuiteFill,
  IconTestSuiteOutline,
  IconTunnelProxiesFill,
  IconTunnelProxiesOutline,
  IconUserFill,
  IconUserOutline,
  IconVisualFill,
  IconVisualOutline,
  IconVisualTestingFill,
  IconVisualTestingOutline,
  IconVpnFill,
  IconVpnOutline,
} from '@saucelabs/ds-icons';
import { operations as authOperations } from '@saucelabs/sl-container-authentication';
import { selectors as concurrencySelectors } from '@saucelabs/sl-module-concurrency';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';
import { SearchModal } from '@saucelabs/sl-component-search-modal';
import { recordTrack } from '@saucelabs/sl-util-segmentio';
import SupportedFeature, {
  constants as features,
  isSupportedFeature,
} from '@saucelabs/sl-component-supported-feature';
import { constants as userConstants, records as userRecords } from '@saucelabs/sl-module-user';
import config from '@saucelabs/sl-util-config';
import { prettyDCName, useMediaQuery } from '@saucelabs/sl-util-helpers';
import { OnboardingSidebarContainer } from '@saucelabs/sl-component-onboarding';
import { Typography } from '@saucelabs/ds-typography';
import { DSThemeContext } from '@saucelabs/ds-theme';
import { Badge } from '@saucelabs/ds-badge';

import {
  connectSidebarTracking,
  getAnalyticsLink,
  isParentOf,
  isVisibleForUserType as isAnalyticsVisibleForUserType,
  useActiveDatacenterList,
  useFocusTarget,
} from '../utils';
import { NAVIGATION_EVENTS, V2_VERSION } from '../tracking';
import * as actions from '../ducks/dashboard-container-new/actions';
import UsageBillingUsageContainer from '../containers/UsageBillingUsageContainer';
import * as constants from '../constants';
import DCExplanationTooltip from './DCExplanationTooltip';
import { TrackingSidebarExpandableMenuItem } from './Navigation/TrackingSidebarExpandableMenuItem';
import { SelectedDCCheckmark } from './Navigation/SelectedDCCheckmark';
import { constants as timeChartConstants, TimeChart, UsageSessionChart } from './Charts';
import { switchActiveTeam as operationSwitchActiveTeam } from '../../application/operations';
import { SKIP_MENU_IDS } from './SkipMenu';

import { haveAccessToMultipleTeams } from '@saucelabs/web-ui-team-management/src/utils';

const { adminUiUrl, CURRENT_DC, statusPageUrl, dataCentersList } = config;

const ExternalIcon = () => (
  <IconExternalOutline className="h-2 w-2 fill-current text-gray-500" />
);

const TrackingSidebarMenuItemChild = connectSidebarTracking(SidebarMenuItemChild);
const TrackingSidebarMenuItem = connectSidebarTracking(SidebarMenuItem);

export const AuthenticatedSidebarNew: React.FC<AuthenticatedSidebarNewProps> = (props) => {
  const {
    closeMobileMenu,
    handleLogout,
    hasUsageSubscription,
    isCollapsed,
    isFreemiumCapable,
    isMobileCollapsed,
    isOnFreeTrialPlan,
    onboardingOrder,
    onboardingSteps,
    onboardingType,
    setShowUsage,
    sidebarShowUsage,
    switchDCCallback,
    user,
    userConcurrency,
    userTunnelsCount,
    switchActiveTeam,
  } = props;
  const location = useLocation();
  const flags = useFlags<{
    errorReportingSidebarNav?: boolean,
    webUiUsageBasedBilling?: boolean,
    webUiLabs?: boolean,
    isSearchAvailable?: boolean,
    growthContinuousOnboarding?: boolean,
    webUiLowCode?: boolean,
    webUiInsightsNewConcurrencyReport?: boolean,
    webUiIrisBeta?: boolean,
    webUiIrisScreener?: boolean,
  }>();
  const isManualOnboarding = onboardingType === 'manual';
  const { matches: isMobile } = useMediaQuery(MOBILE_MEDIA_QUERY);
  const dataCenters = useActiveDatacenterList(dataCentersList);
  const focusProps = useFocusTarget(SKIP_MENU_IDS.SIDEBAR);
  const disabledDataCenters = user && dataCenters ? (
    user?.getDisabledRegions(dataCenters)
  ) : [];
  const showRemainingTimeSection = !isCollapsed &&
    Number.isFinite(user.minutes) &&
    // Only users on trial have valid limit of minutes available
    user.user_type === userConstants.USER_TYPE_FREE_TRIAL;

  const showRdcCharts = userConcurrency.organization?.get('allowed').rdc > 0;
  const showVdcCharts = userConcurrency.organization?.get('allowed').overall > 0;
  const showTeamCharts = userConcurrency.team?.get('allowed');
  const showUsageCharts = (
    (flags?.webUiUsageBasedBilling && user.getOrgUserType() === userConstants.USER_TYPE_FREE) || // free accounts in usage experiment
    (user.getOrgUserType() ===
      userConstants.USER_TYPE_SUBSCRIBED_EXTERNAL && hasUsageSubscription // subscribed usage accounts
    ) ||
    (isFreemiumCapable || isOnFreeTrialPlan) // freemium & new free trial users
  );

  const hasBillingAccess = (
    isSupportedFeature(features.BILLING) && user?.canSelfServe() && user?.isOrgAdmin()
  );

  let showOnboarding = flags?.growthContinuousOnboarding &&
    onboardingOrder?.length > 0 && !!onboardingSteps;

  if (isManualOnboarding && !flags?.growthManualOnboarding) {
    showOnboarding = false;
  }

  // make search visible or invisible
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const errorReportingUrl = config.errorReportingUrl.replace('$userOrgId', user.organization.id);

  return (
    <Sidebar
      aria-label="Application"
      closeMobileMenu={closeMobileMenu}
      content={showOnboarding && (
        <DSThemeContext.Provider value={{ isSDSThemeEnabled: false }}>
          <OnboardingSidebarContainer isMobile={isMobile} />
        </DSThemeContext.Provider>
      )}
      data-qa="sidebar"
      isCollapsed={isCollapsed}
      isMobileCollapsed={isMobileCollapsed}
      {...focusProps}
    >
      {isSupportedFeature(features.GETTING_STARTED) && (
        <TrackingSidebarMenuItem
          label="Get Started Guide"
          productArea="Growth"
          startIcon={{
            icon: IconGettingStartedOutline,
            iconActive: IconGettingStartedFill,
          }}
          to="/getting-started"
        />
      )}
      {(isSupportedFeature(features.APP_MANAGEMENT)) && (
        <TrackingSidebarMenuItem
          label="App Management"
          productArea="App Management"
          startIcon={{
            icon: IconAppForegroundOutline,
            iconActive: IconAppForegroundFill,
          }}
          to="/app-management"
        />
      )}
      {isSupportedFeature(features.LIVE_TESTING) && (
        <TrackingSidebarExpandableMenuItem
          isChildCurrent={isParentOf(location, constants.LIVE_TESTING)}
          label="Live"
          navigationEvent={NAVIGATION_EVENTS.TOGGLE_MANUAL}
          path="/live"
          startIcon={{
            icon: IconManualOutline,
            iconActive: IconManualFill,
          }}
        >
          <TrackingSidebarMenuItemChild
            label="Test Results"
            productArea="Live"
            to="/dashboard/live"
          />
          {isSupportedFeature(features.RDC) && (
            <TrackingSidebarMenuItemChild
              label="Mobile App"
              productArea="Live"
              to="/live/app-testing"
            />
          )}
          <TrackingSidebarMenuItemChild
            label="Cross Browser"
            productArea="Live"
            to="/live/web-testing"
          />
        </TrackingSidebarExpandableMenuItem>
      )}
      {flags.webUiLowCode && (
        <SupportedFeature name="low_code">
          <TrackingSidebarMenuItem
            endAdornment={(
              <Badge
                color="red"
              >
                New
              </Badge>
            )}
            external
            label="Low-Code Testing"
            productArea="Low-Code Testing"
            startIcon={{
              icon: IconTestSuiteOutline,
              iconActive: IconTestSuiteFill,
            }}
            to="/lowcode/"
            useLabelAsPageName
          />
        </SupportedFeature>
      )}
      <TrackingSidebarExpandableMenuItem
        isChildCurrent={isParentOf(location, constants.AUTOMATED)}
        label="Automated"
        navigationEvent={NAVIGATION_EVENTS.TOGGLE_AUTOMATED}
        path="/automated"
        startIcon={{
          icon: IconAutomatedOutline,
          iconActive: IconAutomatedFill,
        }}
      >
        <TrackingSidebarMenuItemChild
          label="Test Results"
          productArea="Automated"
          to="/dashboard/tests"
        />
        <TrackingSidebarMenuItemChild
          label="Builds"
          productArea="Automated"
          to="/dashboard/builds"
        />
        <TrackingSidebarMenuItemChild
          label="Platform Configurator"
          productArea="Growth"
          to="/platform-configurator"
        />
      </TrackingSidebarExpandableMenuItem>
      {isSupportedFeature(features.ANALYTICS) && (
        <TrackingSidebarExpandableMenuItem
          isChildCurrent={isParentOf(location, constants.ANALYTICS)}
          label="Insights"
          navigationEvent={NAVIGATION_EVENTS.TOGGLE_ANALYTICS}
          startIcon={{
            icon: IconAnalyticsOutline,
            iconActive: IconAnalyticsFill,
          }}
        >
          <TrackingSidebarMenuItemChild
            label="Job Overview"
            productArea="Insights"
            to={getAnalyticsLink(location, '/analytics/test-overview')}
            toMatcher="/analytics/test-overview"
          />
          <TrackingSidebarMenuItemChild
            label="Job History"
            productArea="Insights"
            to={(currentLocation) => getAnalyticsLink(currentLocation, '/analytics/test-history')}
            toMatcher="/analytics/test-history"
          />
          <TrackingSidebarMenuItemChild
            label="Trends"
            productArea="Insights"
            to={(currentLocation) => getAnalyticsLink(currentLocation, '/analytics/trends')}
            toMatcher="/analytics/trends/(vdc|rdc)"
          />
          <TrackingSidebarMenuItemChild
            label="Coverage"
            productArea="Insights"
            to={(currentLocation) => getAnalyticsLink(currentLocation, '/analytics/coverage/devices')}
            toMatcher="/analytics/coverage"
          />
          {!flags?.webUiInsightsNewConcurrencyReport &&
          isAnalyticsVisibleForUserType(user.getOrgUserType()) && (
            <TrackingSidebarMenuItemChild
              label="VM Concurrency"
              productArea="Insights"
              to={(currentLocation) => getAnalyticsLink(currentLocation, '/analytics/trends/concurrency')}
              toMatcher="/analytics/trends/concurrency"
            />
          )}
          {flags?.webUiInsightsNewConcurrencyReport && (
            <TrackingSidebarMenuItemChild
              label="Usage"
              postfixIcon={<Badge color="red">New</Badge>}
              productArea="Insights"
              to="/analytics/usage"
              toMatcher="/analytics/usage"
            />
          )}
          <TrackingSidebarMenuItemChild
            label="Failure Analysis"
            productArea="Insights"
            to={(currentLocation) => getAnalyticsLink(currentLocation, '/analytics/test-patterns')}
            toMatcher="/analytics/test-patterns"
          />
        </TrackingSidebarExpandableMenuItem>
      )}
      <SupportedFeature name="visual">
        {flags?.webUiIrisBeta && (
          <TrackingSidebarMenuItem
            endAdornment={(
              <Badge
                color="red"
              >
                New
              </Badge>
            )}
            label="Visual Testing"
            productArea="Visual Testing Iris"
            startIcon={{
              icon: IconVisualTestingOutline,
              iconActive: IconVisualTestingFill,
            }}
            to="/visual"
            useLabelAsPageName
          />
        )}
      </SupportedFeature>
      <SupportedFeature name="visual_testing">
        {flags?.webUiIrisScreener && (
          <TrackingSidebarMenuItem
            endAdornment={<ExternalIcon />}
            external
            label="Screener"
            productArea="Visual Testing"
            startIcon={{
              icon: IconVisualOutline,
              iconActive: IconVisualFill,
            }}
            target="_blank"
            to="https://screener.io/"
            useLabelAsPageName
          />
        )}
      </SupportedFeature>
      <SupportedFeature name="error_reporting">
        {flags?.errorReportingSidebarNav && (
          <TrackingSidebarMenuItem
            endAdornment={(
              <Badge
                color="orange"
              >
                Beta
              </Badge>
            )}
            external
            label="Error Reporting"
            productArea="Error Reporting"
            startIcon={{
              icon: IconErrorReportingOutline,
              iconActive: IconErrorReportingFill,
            }}
            target="_blank"
            to={errorReportingUrl}
            useLabelAsPageName
          />
        )}
      </SupportedFeature>
      <SupportedFeature name="mobile_beta_testing">
        <TrackingSidebarMenuItem
          endAdornment={<ExternalIcon />}
          external
          label="Mobile Beta Testing"
          productArea="TestFairy"
          rel="noopener noreferrer"
          startIcon={{
            icon: IconDevicePhoneOutline,
            iconActive: IconDevicePhoneFill,
          }}
          target="_blank"
          to="https://mobile.saucelabs.com"
          useLabelAsPageName
        />
      </SupportedFeature>
      <SupportedFeature name="apitesting">
        <TrackingSidebarMenuItem
          external
          label="API Testing"
          productArea="API Testing"
          startIcon={{
            icon: IconApiTestingOutline,
            iconActive: IconApiTestingFill,
          }}
          to="/api-testing/"
          useLabelAsPageName
        />
      </SupportedFeature>
      <SupportedFeature name="tunnel_proxies">
        <TrackingSidebarMenuItem
          className="border-0 border-b border-gray-200 border-solid"
          endAdornment={userTunnelsCount && (
            <span
              className="text-green-400 whitespace-nowrap"
            >
              {userTunnelsCount}
              {' '}
              active
            </span>
          )}
          label="Tunnel Proxies"
          productArea="Sauce Connect"
          startIcon={{
            icon: IconTunnelProxiesOutline,
            iconActive: IconTunnelProxiesFill,
          }}
          to="/tunnels"
          useLabelAsPageName
        />
      </SupportedFeature>
      {!isMobile && (
        <SidebarExpandableItem
          data-qa="sidebar-item-usage"
          isExpanded={sidebarShowUsage}
          label="Usage"
          setIsExpanded={setShowUsage}
          startIcon={{
            icon: IconDashboardOutline,
            iconActive: IconDashboardFill,
          }}
        >
          <>
            {showUsageCharts ? (
              <div className="mt-1">
                <UsageBillingUsageContainer
                  // eslint-disable-next-line
                  // @ts-ignore
                  sidebarCollapsed={isCollapsed}
                  user={user}
                  userConcurrency={userConcurrency}
                />
              </div>
            ) : (
              <div
                className={classnames('my-1', {
                  'mx-2': !isCollapsed,
                  'mx-1 text-center': isCollapsed,
                })}
              >
                {showTeamCharts && (
                  <>
                    <Typography
                      className="mt-1 mb-1"
                      disableSpacing
                      variant="overline"
                    >
                      {!isCollapsed ? (
                        <>My Team&apos;s Sessions&nbsp;</>
                      ) : 'Team'}
                    </Typography>
                    {showRdcCharts && (
                      <UsageSessionChart
                        collapsed={isCollapsed}
                        collapsedChartLabel="Real"
                        current={userConcurrency.team.get('current').rdc}
                        label="Public Real Devices"
                        limit={userConcurrency.team.get('allowed').rdc}
                        onClick={() => recordTrack(NAVIGATION_EVENTS.CLICK_USAGE_CONCURRENCY_REAL, {
                          version: V2_VERSION,
                        })}
                        tooltipLabel="Team's Concurrent Sessions: Public Real Devices"
                      />
                    )}
                    {showVdcCharts && (
                      <UsageSessionChart
                        collapsed={isCollapsed}
                        collapsedChartLabel="Virtual"
                        current={userConcurrency.team.get('current').overall}
                        label="Virtual Devices"
                        limit={userConcurrency.team.get('allowed').overall}
                        onClick={() => recordTrack(
                          NAVIGATION_EVENTS.CLICK_USAGE_CONCURRENCY_VIRTUAL, {
                            version: V2_VERSION,
                          })}
                        tooltipLabel="Team's Concurrent Sessions: Virtual Devices"
                      />
                    )}
                  </>
                )}
                <Typography
                  className={classnames('mt-3 mb-1', {
                    'flex items-center': !isCollapsed,
                  })}
                  disableSpacing
                  variant="overline"
                >
                  {!isCollapsed ? (
                    <>
                      <span className="mr-1">Organization Sessions&nbsp;</span>
                      <DCExplanationTooltip />
                    </>
                  ) : 'Org'}
                </Typography>
                {showRdcCharts && (
                  <UsageSessionChart
                    collapsed={isCollapsed}
                    collapsedChartLabel="Real"
                    current={userConcurrency.organization.get('current').rdc}
                    label="Public Real Devices"
                    limit={userConcurrency.organization.get('allowed').rdc}
                    onClick={() => recordTrack(NAVIGATION_EVENTS.CLICK_USAGE_CONCURRENCY_REAL, {
                      version: V2_VERSION,
                    })}
                    tooltipLabel="Organization Concurrent Sessions: Public Real Devices"
                  />
                )}
                {showVdcCharts && (
                  <UsageSessionChart
                    collapsed={isCollapsed}
                    collapsedChartLabel="Virtual"
                    current={userConcurrency.organization.get('current').overall}
                    label="Virtual Devices"
                    limit={userConcurrency.organization.get('allowed').overall}
                    onClick={() => recordTrack(NAVIGATION_EVENTS.CLICK_USAGE_CONCURRENCY_VIRTUAL, {
                      version: V2_VERSION,
                    })}
                    tooltipLabel="Organization Concurrent Sessions: Virtual Devices"
                  />
                )}
                {showRemainingTimeSection && (
                  <>
                    <Typography
                      className={classnames('mt-3 mb-1', {
                        'flex items-center': !isCollapsed,
                      })}
                      disableSpacing
                      variant="overline"
                    >
                      {!isCollapsed ? (
                        <span className="mr-1">Remaining Time (Virtual)&nbsp;</span>
                      ) : 'Time'}
                    </Typography>
                    <TimeChart
                      collapsed={isCollapsed}
                      collapsedProgressGaugeLabel={`Remaining time: ${(timeChartConstants as Record<string, string>).PREFIX_AUTOMATED}`}
                      labelPrefix={(timeChartConstants as Record<string, string>).PREFIX_AUTOMATED}
                      minutesLimit={user.monthly_minutes.automated}
                      minutesRemaining={user.minutes}
                      noLabelSuffix="tests"
                      onClick={() => recordTrack(
                        NAVIGATION_EVENTS.CLICK_USAGE_REMAINING_AUTOMATED, {
                          version: V2_VERSION,
                        })}
                    />
                  </>
                )}
              </div>
            )}
          </>
        </SidebarExpandableItem>
      )}
      {isMobile && (
        <>
          <SidebarMenuItem
            className="beamer-connect"
            label="Product Announcements"
            startIcon={{
              icon: IconBellOutline,
              iconActive: IconBellFill,
            }}
          />
          {flags.isSearchAvailable && (
            <SidebarMenuItem
              className="search"
              label="Search"
              onClick={() => setIsSearchVisible(true)}
              startIcon={{
                icon: IconSearchOutline,
                iconActive: IconSearchFill,
              }}
            />
          )}
          <SidebarExpandableMenuItem
            label="Help and Support"
            startIcon={{
              icon: IconQuestionOutline,
              iconActive: IconQuestionFill,
            }}
          >
            <SidebarMenuItemChild
              external
              label="Support Portal"
              postfixIcon={<ExternalIcon />}
              rel="noopener noreferrer"
              target="_blank"
              to="https://support.saucelabs.com"
            />
            <SidebarMenuItemChild
              external
              label="Documentation"
              postfixIcon={<ExternalIcon />}
              rel="noopener noreferrer"
              target="_blank"
              to="https://docs.saucelabs.com/"
            />
            <SidebarMenuItemChild
              external
              label="Status Page"
              postfixIcon={<ExternalIcon />}
              rel="noopener noreferrer"
              target="_blank"
              to={statusPageUrl}
            />
          </SidebarExpandableMenuItem>
          <SidebarExpandableMenuItem
            label="Account"
            startIcon={{
              icon: IconUserOutline,
              iconActive: IconUserFill,
            }}
          >
            {user.isAdmin() && (
              <SidebarMenuItemChild
                external
                label="Sauce Admin"
                rel="noopener noreferrer"
                target="_blank"
                to={adminUiUrl}
              />
            )}
            {user?.isOrgAdmin() && (
              <SidebarMenuItemChild
                label="Team Management"
                to="/team-management"
              />
            )}
            {!user.isOrgAdmin() && user.isTeamAdmin() && (
              <SidebarMenuItemChild
                label="Team Management"
                to={`/team-management/teams/${user?.getUserTeamId() ?? ''}`}
              />
            )}
            {hasBillingAccess && (
              <SidebarMenuItemChild
                label="Subscription and Billing"
                to="/billing"
              />
            )}
            <SidebarMenuItemChild
              label="User Settings"
              to="/user-settings"
            />
            {isSupportedFeature(features.INTEGRATIONS) &&
              user.get('allow_integrations_page') && (
              <SidebarMenuItemChild
                label="Integrations"
                to="/integrations"
              />
            )}
            <SidebarMenuItemChild
              data-qa="sidebar-nav-logout"
              label="Log Out"
              onClick={() => handleLogout?.()}
            />
          </SidebarExpandableMenuItem>
          {haveAccessToMultipleTeams(user) && user?.teams && (
            <SidebarExpandableMenuItem
              label="Active Team"
              startIcon={{
                icon: IconGroupOutline,
                iconActive: IconGroupFill,
              }}
            >
              {user.teams.map(({ id, name }) => (
                <SidebarMenuItemChild
                  key={id}
                  label={name}
                  onClick={() => {
                    if (id !== user.teams[0].id) {
                      switchActiveTeam({ team: id });
                    }
                  }}
                  postfixIcon={id === user.teams[0].id ? <IconCheckmarkFill className="h-2 w-2 ml-1 fill-current text-blue-700" /> : undefined}
                />
              ))}
            </SidebarExpandableMenuItem>
          )}
          <SidebarExpandableMenuItem
            label={`Data Center: ${prettyDCName(CURRENT_DC)}`}
            labelNode={(
              <>
                Data Center:
                {' '}
                <span className="text-high-emphasis">{prettyDCName(CURRENT_DC)}</span>
              </>
            )}
            startIcon={{
              icon: IconVpnOutline,
              iconActive: IconVpnFill,
            }}
          >
            {dataCenters
              ?.filter((item) => !disabledDataCenters?.includes(item.value))
              ?.map(({ value, url, label }) => (
                <SidebarMenuItemChild
                  key={value}
                  data-qa={`sidebar-nav-dc-${value}`}
                  label={label}
                  onClick={() => {
                    switchDCCallback({
                      dcName: value,
                      dcUrl: url,
                    });
                  }}
                  postfixIcon={value === CURRENT_DC ? <IconCheckmarkFill className="h-2 w-2 ml-1 fill-current text-blue-700" /> : undefined}
                />
              ))}
          </SidebarExpandableMenuItem>
        </>
      )}
      <SearchModal
        indexName="sauce-docs"
        isOpen={isSearchVisible}
        onClose={() => setIsSearchVisible(false)}
      />
    </Sidebar>
  );
};

/**
 * Custom map type to ensure key exists for record, instead of 'V | undefined' provided by maps by
 * default.
 */
type RecordMap<T> = {
  get<K extends keyof T>(key: K): T[K];
} & T;

type ConcurrencyType = RecordMap<{
  current: RecordMap<Record<'vms' | 'rdc' | 'overall' | 'mac_vms', number>>;
  allowed: RecordMap<Record<'vms' | 'rdc' | 'overall' | 'mac_vms', number>>;
}>

export interface AuthenticatedSidebarNewProps {
  closeMobileMenu: () => void;
  handleLogout: () => void;
  hasUsageSubscription: boolean;
  isCollapsed: boolean;
  isFreemiumCapable: boolean;
  isMobileCollapsed: boolean;
  isOnFreeTrialPlan: boolean;
  onboardingOrder: string[];
  onboardingSteps: Record<string, any>;
  onboardingType: string;
  setShowUsage: (isExpanded: boolean) => void;
  sidebarShowUsage?: boolean;
  switchDCCallback: (newDataCenter: { dcName: string; dcUrl: string }) => void;
  user: userRecords.UserRecord;
  userConcurrency: {
    team: ConcurrencyType;
    organization: ConcurrencyType;
  };
  userTunnelsCount?: number;
  switchActiveTeam: ({ team: string }) => void;
}

// TODO: Create better system for managing global store state & types.
// eslint-disable-next-line max-len
/* eslint-disable @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call */
const mapStateToProps = (state) => ({
  isCollapsed: state.dashboard.isCollapsed,
  isMobileCollapsed: state.dashboard.isMobileCollapsed,
  onboardingOrder: state.onboarding?.get('order'),
  onboardingSteps: state.onboarding?.get('steps'),
  onboardingType: state.onboarding?.get('onboardingType'),
  user: state.user,
  userConcurrency: concurrencySelectors.concurrencySelector(state),
  sidebarShowUsage: state.dashboard.showUsage,
  userTunnelsCount: state.userTunnels.getIn(
    ['tunnels', state.user.get('username')],
    tunnelsConstants.NO_TUNNELS_USER_FALLBACK,
  ).reduce((tunnelsCount: number, list: Map<unknown, unknown>) => (
    tunnelsCount + list.count()
  ), 0),
});

const mapDispatchToProps = {
  closeMobileMenu: actions.closeMobileMenu,
  setShowUsage: actions.setShowUsage,
  handleLogout: authOperations.logout,
  switchDCCallback: authOperations.switchDC,
  switchActiveTeam: operationSwitchActiveTeam,
};
// eslint-disable-next-line max-len
/* eslint-enable @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call */

export default connect(mapStateToProps, mapDispatchToProps)(AuthenticatedSidebarNew);
